type PlanService = {
  category?: string
  services?: Array<{ name?: string; nome?: string; quantity?: string; isIncluded?: boolean; isPartial?: boolean }>
  name?: string
  nome?: string
  quantity?: string
  isIncluded?: boolean
}

type PlanInput = {
  nome?: string
  name?: string
  description?: string
  tipo?: string
  duration?: number
  valor?: number
  category?: string
  config?: Record<string, unknown>
  services?: PlanService[]
  items?: unknown[]
  sections?: PlanService[]
  servicos?: PlanService[]
  pricing?: {
    total?: number
    entry?: number
    installments?: number
    installmentValue?: number
  }
}

export type MergedPlan = {
  nome: string
  name: string
  description: string
  tipo: string
  duration: number
  services: PlanService[]
  pricing: {
    total: number
    entry: number
    installments: number
    installmentValue: number
  }
  valor?: number
  comboGroupId?: string
  id?: string
}

function getServices(p: PlanInput): PlanService[] {
  if (!p) return []
  const cfg = p.config || {}

  if (Array.isArray(p.services) && p.services.length > 0) return p.services
  if (Array.isArray(cfg.services) && (cfg.services as PlanService[]).length > 0) {
    return cfg.services as PlanService[]
  }

  const flatItems = Array.isArray(cfg.deliverables)
    ? cfg.deliverables
    : Array.isArray(p.items)
      ? p.items
      : Array.isArray(cfg.items)
        ? cfg.items
        : null

  if (flatItems && flatItems.length > 0) {
    return [
      {
        category: p.category || (p.nome || p.name || '').split(' ')[0] || 'Geral',
        services: (flatItems as Array<Record<string, unknown>>).map((d) => ({
          name: String(d.name || d.nome || d.label || 'Serviço'),
          quantity: d.quantity ? String(d.quantity) : d.qtd ? String(d.qtd) : undefined,
          isIncluded: true,
        })),
      },
    ]
  }

  if (Array.isArray(p.sections)) return p.sections
  if (Array.isArray(p.servicos)) return p.servicos
  return []
}

export function mergePlans(
  planos: PlanInput[],
  globalDesconto = 0,
  dealName = 'Plano Corporativo',
): MergedPlan | null {
  if (!planos || planos.length === 0) return null

  const mergedPlan: MergedPlan = {
    nome: dealName,
    name: dealName,
    description:
      'Pacote de serviços estrategicamente desenhado em formato de Combo para cobrir todas as frentes comerciais e técnicas solicitadas.',
    tipo: planos.some((p) => p.tipo === 'recorrente') ? 'recorrente' : 'setup',
    duration: Math.max(...planos.map((p) => Number(p.duration || 12))),
    services: [],
    pricing: { total: 0, entry: 0, installments: 0, installmentValue: 0 },
  }

  const categoryMap = new Map<string, PlanService>()

  planos.forEach((plano) => {
    const valTotal = Number(plano.valor || plano.pricing?.total || 0)
    const pDur = Number(plano.duration || 12)

    mergedPlan.pricing.total += valTotal
    mergedPlan.pricing.entry += Number(plano.pricing?.entry || 0)

    if (plano.tipo === 'recorrente' || (plano.pricing && Number(plano.pricing.installmentValue) > 0)) {
      const instVal = Number(plano.pricing?.installmentValue || valTotal / pDur)
      mergedPlan.pricing.installmentValue += instVal
      mergedPlan.pricing.installments = Math.max(
        mergedPlan.pricing.installments,
        Number(plano.pricing?.installments || pDur),
      )
    }

    const planoName = (plano.nome || plano.name || 'Servico').trim()
    const svcs = getServices(plano)

    svcs.forEach((item) => {
      if (!item) return

      if (item.category && Array.isArray(item.services)) {
        const catName = `${planoName} ::: ${item.category.trim()}`
        if (!categoryMap.has(catName)) {
          categoryMap.set(catName, { category: catName, services: [] })
        }
        const existingCat = categoryMap.get(catName)!
        item.services.forEach((s) => {
          const sName = (s.name || s.nome || '').trim()
          if (!sName) return
          const exists = existingCat.services!.find((es) => es.name === sName)
          if (!exists) {
            existingCat.services!.push({ ...s, name: sName })
          }
        })
      } else if (item.name || item.nome) {
        const sName = (item.name || item.nome || '').trim()
        if (!sName) return
        const catName = `${planoName} ::: Geral`
        if (!categoryMap.has(catName)) {
          categoryMap.set(catName, { category: catName, services: [] })
        }
        const existingCat = categoryMap.get(catName)!
        const exists = existingCat.services!.find((es) => es.name === sName)
        if (!exists) {
          existingCat.services!.push({
            name: sName,
            quantity: item.quantity,
            isIncluded: item.isIncluded !== false,
          })
        }
      }
    })
  })

  if (globalDesconto > 0) {
    const descMult = 1 - globalDesconto / 100
    mergedPlan.pricing.total *= descMult
    mergedPlan.pricing.installmentValue *= descMult
    mergedPlan.pricing.entry *= descMult
  }

  mergedPlan.services = Array.from(categoryMap.values())
  mergedPlan.valor = mergedPlan.pricing.total
  return mergedPlan
}
